import { Col, Row, Text } from 'components';
import { Api } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from 'screens/Admin/components';
import { FilterBar, LogTable, Pagination, SecurityNotice, SummaryBar } from './components';
import { DEFAULT_FILTERS, PAGE_SIZE, exportToCsv } from './components/helpers';

const Reporting = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    fetchLogs(appliedFilters, 1, pageSize);
  }, [appliedFilters]);

  async function fetchLogs(f, p, limit) {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: limit ?? pageSize });
    if (f.from) params.append('from', f.from);
    if (f.to) params.append('to', f.to);
    if (f.eventType && f.eventType !== 'all') params.append('eventType', f.eventType);
    if (f.status && f.status !== 'all') params.append('status', f.status);
    if (f.subscriptionPlanName) params.append('subscriptionPlanName', f.subscriptionPlanName);

    const res = await Api.get(`/admin/reporting/webhookLogs?${params.toString()}`);
    if (res?.data) {
      setData(res.data);
      setPage(p);
    }
    setLoading(false);
  }

  function handlePageSizeChange(newSize) {
    setPageSize(newSize);
    fetchLogs(appliedFilters, 1, newSize);
  }

  function handleFilterChange(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleApply() {
    setAppliedFilters(filters);
  }

  function handleClear() {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  }

  function handleFilterByPlan(planName) {
    const newFilters = { ...filters, subscriptionPlanName: planName };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const EXPORT_LIMIT = 100; // API maximum
      let allRows = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const params = new URLSearchParams({ page: currentPage, limit: EXPORT_LIMIT });
        if (appliedFilters.from) params.append('from', appliedFilters.from);
        if (appliedFilters.to) params.append('to', appliedFilters.to);
        if (appliedFilters.eventType && appliedFilters.eventType !== 'all')
          params.append('eventType', appliedFilters.eventType);
        if (appliedFilters.status && appliedFilters.status !== 'all')
          params.append('status', appliedFilters.status);
        if (appliedFilters.subscriptionPlanName)
          params.append('subscriptionPlanName', appliedFilters.subscriptionPlanName);

        const res = await Api.get(`/admin/reporting/webhookLogs?${params.toString()}`);
        if (!res?.data?.rows) break;

        allRows = allRows.concat(res.data.rows);
        totalPages = res.data?.pagination?.totalPages ?? 1;
        currentPage++;
      } while (currentPage <= totalPages);

      if (allRows.length > 0) exportToCsv(allRows);
    } finally {
      setExporting(false);
    }
  }

  const totalCount = data?.pagination?.total ?? data?.total ?? 0;
  // Prefer the API's own totalPages when available and consistent with current pageSize
  const apiTotalPages = data?.pagination?.totalPages;
  const apiLimit = data?.pagination?.limit;
  const totalPages =
    apiTotalPages && apiLimit === pageSize
      ? apiTotalPages
      : totalCount > 0
      ? Math.max(1, Math.ceil(totalCount / pageSize))
      : 1;

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center between wid="100%">
          <Text h3>{t('admin.reporting.webhooks.title')}</Text>
        </Row>
      </Header>

      <Col start noFlex wid="100%" className="mt-[54px] h-[calc(100vh-54px)] overflow-y-auto">
        <div className="p-6 max-w-[1400px] mx-auto w-full">
          <SecurityNotice />

          <FilterBar
            filters={filters}
            onChange={handleFilterChange}
            onApply={handleApply}
            onClear={handleClear}
            eventTypes={data?.availableEventTypes ?? []}
          />

          <SummaryBar summary={data?.summary} />

          {/* Toolbar: result count + export */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-[12px] text-[#97A3B5]">
              {totalCount > 0
                ? t('admin.reporting.webhooks.totalResults', { count: totalCount })
                : ''}
            </p>
            <button
              onClick={handleExport}
              disabled={exporting || loading || !data?.rows?.length}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#D7DEE8] text-[12px] font-medium text-[#475569] hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {exporting
                ? t('admin.reporting.webhooks.exporting')
                : t('admin.reporting.webhooks.exportCsv')}
            </button>
          </div>

          <LogTable rows={data?.rows ?? []} loading={loading} onFilterByPlan={handleFilterByPlan} />

          {!loading && data && (
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              onPrev={() => fetchLogs(appliedFilters, page - 1, pageSize)}
              onNext={() => fetchLogs(appliedFilters, page + 1, pageSize)}
              onGoToPage={(p) => fetchLogs(appliedFilters, p, pageSize)}
            />
          )}

          {/* Payload hidden notice */}
          <div className="mt-6 flex items-start gap-2 text-[11px] text-[#97A3B5]">
            <svg
              className="w-3.5 h-3.5 mt-0.5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>{t('admin.reporting.webhooks.payloadNotice')}</span>
          </div>
        </div>
      </Col>
    </Col>
  );
};

export default Reporting;
